import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import {
  ConnectAccountManagement,
  ConnectAccountOnboarding,
  ConnectBalances,
  ConnectComponentsProvider,
  ConnectNotificationBanner,
  ConnectPayments,
  ConnectPayouts
} from "@stripe/react-connect-js";

const PAYMENT_SECTIONS = ["overview", "money", "stripe"];
const REVENUE_PLOT_WIDTH = 720;
const REVENUE_PLOT_HEIGHT = 240;
const REVENUE_PLOT_PADDING = 54;
const PAYMENT_DATE_PRESETS = [
  { key: "last7", label: "Last 7 days" },
  { key: "thisMonth", label: "This month" },
  { key: "last30", label: "Last 30 days" },
  { key: "all", label: "All records" }
];

function formatMoney(amountCents = 0, currency = "usd") {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: String(currency || "usd").toUpperCase()
  }).format(Number(amountCents || 0) / 100);
}

function formatDateTime(value) {
  return value ? new Date(value).toLocaleString() : "Not recorded";
}

function statusLabel(value) {
  if (!value) {
    return "Recorded";
  }
  const labels = {
    not_required: "Not Required",
    "membership_signup_intent.fulfilled": "Membership payment fulfilled",
    "membership_signup_intent.created": "Membership application started",
    "payment.checkout_completed": "Checkout completed",
    "event_registration.payment_fulfilled": "Event payment fulfilled",
    membership_purchase: "Membership receipt",
    event_registration: "Event registration receipt",
    resource_reservation: "Reservation receipt"
  };
  const key = String(value || "").trim();
  return (labels[key] || key)
    .replace(/[._-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function paymentStatusTone(value = "") {
  const normalized = String(value || "").toLowerCase();
  if (["paid", "succeeded", "complete", "completed", "ready", "live"].some((token) => normalized.includes(token))) {
    return "is-success";
  }
  if (["open", "pending", "processing", "requires", "unpaid"].some((token) => normalized.includes(token))) {
    return "is-warning";
  }
  if (["failed", "canceled", "cancelled", "dispute", "refund", "past due"].some((token) => normalized.includes(token))) {
    return "is-danger";
  }
  return "";
}

function PaymentStatusChip({ status }) {
  return (
    <span className={`payments-status-chip ${paymentStatusTone(status)}`}>
      {statusLabel(status)}
    </span>
  );
}

function PaymentEmptyState({ title, detail }) {
  return (
    <div className="payments-empty-state">
      <strong>{title}</strong>
      {detail && <span>{detail}</span>}
    </div>
  );
}

function receiptLabel(receipt = {}) {
  if (receipt.description) {
    return receipt.description;
  }
  const line = receiptLine(receipt);
  if (line === "Donations") {
    return "Donation";
  }
  if (line === "Reservations") {
    return "Reservation";
  }
  if (line === "Events") {
    return "Event";
  }
  if (line === "Membership") {
    return "Membership";
  }
  return receipt.type || "Payment";
}

function receiptAmountCents(receipt = {}) {
  return Number(receipt.amountCents ?? receipt.amount ?? receipt.total ?? 0);
}

function eventAmountCents(event = {}) {
  return Number(event.amountCents ?? event.amount ?? event.total ?? 0);
}

function receiptLine(receipt = {}) {
  const type = String(
    receipt.entityType ||
    receipt.rangegridEntityType ||
    receipt.metadata?.rangegridEntityType ||
    receipt.type ||
    receipt.lineItems?.[0]?.kind ||
    ""
  ).toLowerCase();

  if (receipt.donationId || type.includes("donation")) {
    return "Donations";
  }
  if (receipt.reservationId || type.includes("reservation")) {
    return "Reservations";
  }
  if (receipt.registrationId || receipt.eventId || type.includes("event")) {
    return "Events";
  }
  if (receipt.membershipTypeId || receipt.memberId || receipt.signupIntentId || type.includes("membership")) {
    return "Membership";
  }
  return "Other";
}

function ownerLineLabel(line = "Other") {
  if (line === "Membership") {
    return "Membership";
  }
  if (line === "Events") {
    return "Event";
  }
  if (line === "Reservations") {
    return "Reservation";
  }
  if (line === "Donations") {
    return "Donation";
  }
  return "Payment";
}

function normalizePaymentSection(value) {
  if (value === "events" || value === "receipts") {
    return "money";
  }
  return PAYMENT_SECTIONS.includes(value) ? value : "overview";
}

function eventLine(event = {}) {
  const type = String(event.type || event.eventType || "").toLowerCase();
  if (event.donationId || type.includes("donation")) {
    return "Donations";
  }
  if (event.reservationId || type.includes("reservation")) {
    return "Reservations";
  }
  if (event.registrationId || event.eventId || type.includes("event")) {
    return "Events";
  }
  if (event.membershipTypeId || event.memberId || event.signupIntentId || type.includes("membership")) {
    return "Membership";
  }
  return "Other";
}

function customerLabel(record = {}) {
  return record.email || record.customerEmail || record.checkoutEmail || record.memberEmail || record.customerName || "No customer recorded";
}

function isPaidReceipt(receipt = {}) {
  return ["paid", "succeeded", "complete", "completed"].includes(String(receipt.status || "").toLowerCase());
}

function isPeriodCloseReceipt(receipt = {}) {
  return ["paid", "succeeded", "complete", "completed", "partially_refunded", "refunded"].includes(String(receipt.status || "").toLowerCase());
}

function isRefundableReceiptStatus(receipt = {}) {
  return ["paid", "succeeded", "complete", "completed", "partially_refunded"].includes(String(receipt.status || "").toLowerCase());
}

function refundedAmountCents(receipt = {}) {
  return Number(receipt.refundedAmountCents || 0);
}

function receiptLedgerNetCents(receipt = {}) {
  const value = receipt.reconciliation?.ledgerNetCents ?? receipt.stripeNetCents;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : refundableAmountCents(receipt);
}

function refundableAmountCents(receipt = {}) {
  return Math.max(0, receiptAmountCents(receipt) - refundedAmountCents(receipt));
}

function canRefundReceipt(receipt = {}) {
  return receipt.provider === "stripe" &&
    Boolean(receipt.receiptId && receipt.paymentIntentId) &&
    isRefundableReceiptStatus(receipt) &&
    refundableAmountCents(receipt) > 0;
}

function canRefundMoneyRow(row = {}) {
  return row.source === "Receipt" && canRefundReceipt(row.raw || {});
}

function receiptReconciliationReview(receipt = {}) {
  return receipt.reconciliation?.review || receipt.reconciliationReview || {};
}

function reconciliationReviewLabel(status = "") {
  const labels = {
    open: "Open review",
    reviewed: "Reviewed",
    needs_follow_up: "Needs follow-up"
  };
  return labels[String(status || "open").toLowerCase()] || "Open review";
}

function isPendingReceipt(receipt = {}) {
  return ["open", "pending", "unpaid", "processing", "requires_payment"].includes(String(receipt.status || "").toLowerCase());
}

function isAttentionEvent(event = {}) {
  const text = [
    event.type,
    event.eventType,
    event.paymentStatus,
    event.subscriptionStatus,
    event.status
  ].filter(Boolean).join(" ").toLowerCase();
  return ["failed", "refund", "dispute", "requires", "past_due", "canceled"].some((token) => text.includes(token));
}

function recordDate(record = {}) {
  return record.createdAt || record.updatedAt || record.paidAt || record.timestamp || "";
}

function businessLineTotals(receipts) {
  return ["Membership", "Events", "Reservations", "Donations", "Other"].map((label) => {
    const lineReceipts = receipts.filter((receipt) => receiptLine(receipt) === label);
    return {
      label,
      count: lineReceipts.length,
      amountCents: lineReceipts
        .filter(isPaidReceipt)
        .reduce((total, receipt) => total + receiptAmountCents(receipt), 0)
    };
  });
}

function summarizePaidReceipts(receipts, predicate) {
  const matching = receipts.filter((receipt) => isPaidReceipt(receipt) && predicate(receipt));
  const amountCents = matching.reduce((total, receipt) => total + receiptAmountCents(receipt), 0);
  return {
    amountCents,
    count: matching.length,
    averageCents: matching.length ? Math.round(amountCents / matching.length) : 0
  };
}

function searchMoneyRows(rows, query) {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return [];
  }
  return rows.filter((row) => [
    row.title,
    row.line,
    row.source,
    row.customer,
    row.reference,
    row.secondaryReference,
    row.status
  ].filter(Boolean).join(" ").toLowerCase().includes(normalized));
}

function formatPlotDate(value) {
  if (!value) {
    return "";
  }
  return new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric"
  });
}

function localDayKey(value) {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0")
  ].join("-");
}

function dateInputValue(date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0")
  ].join("-");
}

function dateFromInput(value, endOfDay = false) {
  const [year, month, day] = String(value || "").split("-").map(Number);
  if (!year || !month || !day) {
    return null;
  }
  return new Date(year, month - 1, day, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0);
}

function getPresetDateRange(preset, receipts = []) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(today);

  if (preset === "thisMonth") {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    return { start: dateInputValue(start), end: dateInputValue(end) };
  }

  if (preset === "last30") {
    const start = new Date(today);
    start.setDate(start.getDate() - 29);
    return { start: dateInputValue(start), end: dateInputValue(end) };
  }

  if (preset === "all") {
    const datedReceipts = receipts
      .map((receipt) => new Date(recordDate(receipt)))
      .filter((date) => !Number.isNaN(date.getTime()))
      .sort((a, b) => a - b);
    if (datedReceipts.length > 0) {
      return {
        start: dateInputValue(datedReceipts[0]),
        end: dateInputValue(datedReceipts[datedReceipts.length - 1])
      };
    }
  }

  const start = new Date(today);
  start.setDate(start.getDate() - 6);
  return { start: dateInputValue(start), end: dateInputValue(end) };
}

function isWithinDateRange(value, range) {
  const date = new Date(value);
  const start = dateFromInput(range?.start);
  const end = dateFromInput(range?.end, true);
  if (Number.isNaN(date.getTime()) || !start || !end) {
    return false;
  }
  return date >= start && date <= end;
}

function formatDateRangeLabel(range) {
  const start = dateFromInput(range?.start);
  const end = dateFromInput(range?.end);
  if (!start || !end) {
    return "Selected dates";
  }
  const format = { month: "short", day: "numeric" };
  const startText = start.toLocaleDateString(undefined, format);
  const endText = end.toLocaleDateString(undefined, format);
  return startText === endText ? startText : `${startText} - ${endText}`;
}

function buildRevenuePlotData(receipts) {
  const byDay = new Map();
  receipts.filter(isPaidReceipt).forEach((receipt) => {
    const key = localDayKey(recordDate(receipt));
    if (!key) {
      return;
    }
    const current = byDay.get(key) || { key, amountCents: 0, count: 0, currency: receipt.currency || "usd" };
    current.amountCents += receiptAmountCents(receipt);
    current.count += 1;
    byDay.set(key, current);
  });

  return Array.from(byDay.values())
    .sort((a, b) => new Date(a.key) - new Date(b.key))
    .slice(-14);
}

function buildMoneyRows(receipts, events) {
  const receiptReferences = new Set();
  const receiptRows = receipts.map((receipt, index) => {
    [
      receipt.checkoutSessionId,
      receipt.paymentIntentId,
      receipt.subscriptionId,
      receipt.receiptId
    ].filter(Boolean).forEach((value) => receiptReferences.add(String(value)));

    return {
      key: `receipt:${receipt.receiptId || receipt.checkoutSessionId || index}`,
      source: "Receipt",
      line: receiptLine(receipt),
      title: receiptLabel(receipt),
      amountCents: receiptAmountCents(receipt),
      currency: receipt.currency || "usd",
      status: statusLabel(receipt.status),
      date: recordDate(receipt),
      customer: customerLabel(receipt),
      reference: receipt.checkoutSessionId || receipt.paymentIntentId || receipt.subscriptionId || receipt.receiptId,
      secondaryReference: receipt.receiptId,
      raw: receipt
    };
  });

  const eventRows = events
    .filter((event) => {
      const references = [event.checkoutSessionId, event.paymentIntentId, event.subscriptionId, event.auditId].filter(Boolean);
      return references.every((value) => !receiptReferences.has(String(value)));
    })
    .map((event, index) => ({
      key: `event:${event.auditId || event.checkoutSessionId || index}`,
      source: "Event",
      line: eventLine(event),
      title: statusLabel(event.type || event.eventType || "Payment event"),
      amountCents: eventAmountCents(event),
      currency: event.currency || "usd",
      status: statusLabel(event.paymentStatus || event.subscriptionStatus || event.status),
      date: recordDate(event),
      customer: customerLabel(event),
      reference: event.checkoutSessionId || event.paymentIntentId || event.subscriptionId || event.auditId,
      secondaryReference: event.auditId,
      raw: event
    }));

  return [...receiptRows, ...eventRows].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
}

function RevenuePlot({
  datePreset,
  dateRange,
  onDatePresetChange,
  onDateRangeChange,
  rangeLabel,
  revenueLines,
  series,
  summary
}) {
  const currency = series[0]?.currency || "usd";

  const plotWidth = REVENUE_PLOT_WIDTH - (REVENUE_PLOT_PADDING * 2);
  const plotHeight = REVENUE_PLOT_HEIGHT - (REVENUE_PLOT_PADDING * 2);
  const hasSeries = series.length > 0;
  const maxAmount = Math.max(...series.map((point) => point.amountCents), 1);
  const yTicks = [1, 0.5, 0].map((ratio) => ({
    ratio,
    y: REVENUE_PLOT_PADDING + ((1 - ratio) * plotHeight),
    label: formatMoney(Math.round(maxAmount * ratio), currency)
  }));
  const xFor = (index) => REVENUE_PLOT_PADDING + (series.length === 1 ? plotWidth / 2 : (index / (series.length - 1)) * plotWidth);
  const yFor = (amountCents) => REVENUE_PLOT_HEIGHT - REVENUE_PLOT_PADDING - ((amountCents / maxAmount) * plotHeight);
  const baselineY = REVENUE_PLOT_HEIGHT - REVENUE_PLOT_PADDING;
  const points = series.map((point, index) => ({
    ...point,
    x: xFor(index),
    y: yFor(point.amountCents)
  }));
  const linePath = points.map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`).join(" ");
  const areaPath = hasSeries ? `${linePath} L ${points[points.length - 1].x} ${baselineY} L ${points[0].x} ${baselineY} Z` : "";
  const barWidth = Math.min(34, Math.max(10, plotWidth / Math.max(series.length * 1.8, 1)));
  const peak = hasSeries ? series.reduce((current, point) => point.amountCents > current.amountCents ? point : current, series[0]) : null;
  const lastPoint = hasSeries ? series[series.length - 1] : null;
  const previousPoint = series.length > 1 ? series[series.length - 2] : null;
  const trendLabel = !lastPoint
    ? "No confirmed activity"
    : previousPoint
      ? `${lastPoint.amountCents >= previousPoint.amountCents ? "Up" : "Down"} ${formatMoney(Math.abs(lastPoint.amountCents - previousPoint.amountCents), currency)} from prior paid day`
      : "First confirmed paid day in this range";
  const maxRevenueLineAmount = Math.max(...revenueLines.map((line) => line.amountCents), 1);

  return (
    <div className="payments-revenue-plot">
      <div className="payments-plot-header">
        <div>
          <span className="section-kicker">Receipt-backed activity</span>
          <h3>Payment activity</h3>
          <p>Confirmed receipts for {rangeLabel}. Non-receipt activity is excluded from this chart.</p>
        </div>
        <div className="payments-plot-total">
          <span>Total collected</span>
          <strong>{formatMoney(summary.amountCents, currency)}</strong>
          <small>{summary.count} payment{summary.count === 1 ? "" : "s"} / avg {formatMoney(summary.averageCents, currency)}</small>
        </div>
      </div>

      <div className="payments-date-controls">
        <label>
          <span>Quick range</span>
          <select value={datePreset} onChange={(event) => onDatePresetChange(event.target.value)}>
            {datePreset === "custom" && <option value="custom">Custom dates</option>}
            {PAYMENT_DATE_PRESETS.map((preset) => (
              <option key={preset.key} value={preset.key}>{preset.label}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Start date</span>
          <input
            type="date"
            value={dateRange.start}
            onChange={(event) => onDateRangeChange("start", event.target.value)}
          />
        </label>
        <label>
          <span>End date</span>
          <input
            type="date"
            value={dateRange.end}
            onChange={(event) => onDateRangeChange("end", event.target.value)}
          />
        </label>
      </div>

      {hasSeries ? (
        <div className="payments-plot-frame">
          <svg
            className="payments-plot-svg"
            role="img"
            viewBox={`0 0 ${REVENUE_PLOT_WIDTH} ${REVENUE_PLOT_HEIGHT}`}
            aria-label={`Paid receipt revenue across ${series.length} payment days`}
          >
            <defs>
              <linearGradient id="paymentsRevenueArea" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#9ebf77" stopOpacity="0.34" />
                <stop offset="100%" stopColor="#f7fbf2" stopOpacity="0.08" />
              </linearGradient>
              <linearGradient id="paymentsRevenueBar" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#c26b2d" stopOpacity="0.92" />
                <stop offset="100%" stopColor="#6f7f4d" stopOpacity="0.86" />
              </linearGradient>
            </defs>
            {yTicks.map((tick) => (
              <g key={tick.ratio}>
                <line
                  className="payments-plot-gridline"
                  x1={REVENUE_PLOT_PADDING}
                  x2={REVENUE_PLOT_WIDTH - REVENUE_PLOT_PADDING}
                  y1={tick.y}
                  y2={tick.y}
                />
                <line
                  className="payments-plot-tick"
                  x1={REVENUE_PLOT_PADDING - 5}
                  x2={REVENUE_PLOT_PADDING}
                  y1={tick.y}
                  y2={tick.y}
                />
                <text className="payments-plot-y-label" x={REVENUE_PLOT_PADDING - 10} y={tick.y + 4} textAnchor="end">
                  {tick.label}
                </text>
              </g>
            ))}
            <line
              className="payments-plot-axis"
              x1={REVENUE_PLOT_PADDING}
              x2={REVENUE_PLOT_PADDING}
              y1={REVENUE_PLOT_PADDING}
              y2={baselineY}
            />
            {points.length > 1 && <path className="payments-plot-area" d={areaPath} />}
            {points.map((point) => (
              <g className="payments-plot-column" key={`bar-${point.key}`}>
                <rect
                  className="payments-plot-bar"
                  x={point.x - (barWidth / 2)}
                  y={point.y}
                  width={barWidth}
                  height={Math.max(4, baselineY - point.y)}
                  rx="4"
                />
              </g>
            ))}
            {points.length > 1 && <path className="payments-plot-line" d={linePath} />}
            {points.map((point) => (
              <g key={point.key}>
                <circle className="payments-plot-point" cx={point.x} cy={point.y} r="4" />
                <title>{formatPlotDate(point.key)} / {formatMoney(point.amountCents, point.currency)} / {point.count} receipts</title>
              </g>
            ))}
            {points.map((point, index) => (
              (points.length <= 7 || index === 0 || index === points.length - 1) && (
                <text className="payments-plot-x-label" textAnchor="middle" x={point.x} y={baselineY + 24} key={`label-${point.key}`}>
                  {formatPlotDate(point.key)}
                </text>
              )
            ))}
          </svg>
          <div className="payments-plot-intel">
            <span>{trendLabel}</span>
            <strong>{peak ? `Peak ${formatPlotDate(peak.key)} / scale tops at ${formatMoney(maxAmount, currency)}` : "No peak yet"}</strong>
          </div>
        </div>
      ) : (
        <div className="payments-plot-empty">
          <strong>No paid receipts in this date range</strong>
          <span>Use the date controls above or wait for an online payment to confirm a receipt.</span>
        </div>
      )}

      <div className="payments-plot-details">
        <div className="payments-plot-facts">
          <div>
            <span>Days with payments</span>
            <strong>{series.length}</strong>
          </div>
          <div>
            <span>Best day</span>
            <strong>{peak ? formatMoney(peak.amountCents, peak.currency) : formatMoney(0, currency)}</strong>
            {peak && <small>{formatPlotDate(peak.key)}</small>}
          </div>
          <div>
            <span>Payments counted</span>
            <strong>{summary.count}</strong>
          </div>
        </div>

        <div className="payments-revenue-mix">
          <div>
            <h4>Where payments came from</h4>
            <p>Paid receipts for {rangeLabel}.</p>
          </div>
          <div className="payments-revenue-mix-list">
            {revenueLines.length === 0 ? (
              <div>
                <span>No paid receipts</span>
                <strong>{formatMoney(0, currency)}</strong>
              </div>
            ) : revenueLines.map((line) => (
              <div key={line.label}>
                <span>
                  <strong>{line.label}</strong>
                  <small>{line.count} payment{line.count === 1 ? "" : "s"}</small>
                </span>
                <em aria-hidden="true">
                  <i style={{ width: `${Math.max(8, Math.round((line.amountCents / maxRevenueLineAmount) * 100))}%` }} />
                </em>
                <b>{formatMoney(line.amountCents, currency)}</b>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function launchReadinessLabel(status) {
  if (status === "done") {
    return "Ready";
  }
  if (status === "waiting") {
    return "Waiting";
  }
  return "Action";
}

function PaymentLaunchChecklist({ environment, items }) {
  const modeLabel = environment === "live"
    ? "Live mode"
    : environment === "test"
      ? "Test mode"
      : "Not configured";

  return (
    <div className="payments-launch-checklist" aria-label="Payment launch checklist">
      <div className="payments-launch-checklist-header">
        <div>
          <h3>Before paid launch</h3>
          <p>Test mode is safe for demos. Use live mode before charging members.</p>
        </div>
        <span className={`payments-mode-badge ${environment === "live" ? "is-live" : "is-test"}`}>
          {modeLabel}
        </span>
      </div>
      <div className="payments-launch-checklist-grid">
        {items.map((item) => (
          <div className={`payments-launch-checklist-item is-${item.status}`} key={item.key}>
            <span>{launchReadinessLabel(item.status)}</span>
            <strong>{item.label}</strong>
            <small>{item.detail}</small>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PaymentsPage({
  closingReconciliation,
  exportingReceipts,
  exportingReconciliation,
  loading,
  onCloseReconciliation,
  onRefreshStripeConnect,
  onStartStripeConnect,
  onFetchStripeConnectClientSecret,
  onExportReceipts,
  onExportPeriodCloses,
  onExportReconciliation,
  onDownloadReceipt,
  onRefundReceipt,
  onSyncReconciliation,
  onUpdateReceiptReconciliation,
  paymentEvents,
  paymentIssues,
  paymentReceipts,
  paymentSettings,
  refreshingStripeConnect,
  reconcilingReceiptId,
  refundingReceiptId,
  selectedRange,
  syncingReconciliation,
  startingStripeConnect,
  stripeConnectSession,
  onStripeConnectExit
}) {
  const [connectComponent, setConnectComponent] = useState("account-onboarding");
  const [connectInstance, setConnectInstance] = useState(null);
  const [connectLoadError, setConnectLoadError] = useState("");
  const [connectLoading, setConnectLoading] = useState(false);
  const [datePreset, setDatePreset] = useState("last7");
  const [dateRange, setDateRange] = useState(() => getPresetDateRange("last7"));
  const [historyDatePreset, setHistoryDatePreset] = useState("last30");
  const [historyDateRange, setHistoryDateRange] = useState(() => getPresetDateRange("last30"));
  const [moneyHistoryLookup, setMoneyHistoryLookup] = useState("");
  const [autoStripeSessionRequested, setAutoStripeSessionRequested] = useState(false);
  const [refundEditorKey, setRefundEditorKey] = useState("");
  const [refundDraft, setRefundDraft] = useState({
    amount: "",
    reason: "requested_by_customer",
    note: ""
  });
  const [refundError, setRefundError] = useState("");
  const [reconciliationEditorKey, setReconciliationEditorKey] = useState("");
  const [reconciliationDraft, setReconciliationDraft] = useState({
    status: "reviewed",
    note: ""
  });
  const [reconciliationError, setReconciliationError] = useState("");
  const [reconciliationSyncResult, setReconciliationSyncResult] = useState(null);
  const [reconciliationSyncError, setReconciliationSyncError] = useState("");
  const [periodCloseOpen, setPeriodCloseOpen] = useState(false);
  const [periodCloseDraft, setPeriodCloseDraft] = useState({
    depositAmount: "",
    depositDate: "",
    depositReference: ""
  });
  const [periodCloseResult, setPeriodCloseResult] = useState(null);
  const [periodCloseError, setPeriodCloseError] = useState("");
  const connectClientSecretRef = useRef("");
  const [activePaymentSection, setActivePaymentSection] = useState(() => {
    if (typeof window === "undefined") {
      return "overview";
    }
    const hash = window.location.hash.replace("#payments-", "");
    return normalizePaymentSection(hash);
  });

  const stripe = useMemo(() => paymentSettings?.stripe || {}, [paymentSettings?.stripe]);
  const receipts = useMemo(() => paymentReceipts?.receipts || [], [paymentReceipts]);
  const events = useMemo(() => paymentEvents?.events || [], [paymentEvents]);
  const issues = useMemo(() => paymentIssues?.issues || [], [paymentIssues]);
  const periodCloses = useMemo(() => paymentSettings?.accounting?.periodCloses || [], [paymentSettings]);
  const paidReceipts = useMemo(() => receipts.filter(isPaidReceipt), [receipts]);
  const pendingReceipts = useMemo(() => receipts.filter(isPendingReceipt), [receipts]);
  const attentionEvents = useMemo(() => events.filter(isAttentionEvent), [events]);
  const paidTotal = useMemo(
    () => paidReceipts.reduce((total, receipt) => total + receiptAmountCents(receipt), 0),
    [paidReceipts]
  );
  const moneyRows = useMemo(() => buildMoneyRows(receipts, events), [events, receipts]);
  const accountSession = stripeConnectSession || paymentSettings?.accountSession || null;
  const connectReady = Boolean(accountSession?.clientSecret && accountSession?.publishableKey);
  const stripeSettingsLoading = loading && !paymentSettings;
  const stripeDetailsSubmitted = Boolean(stripe.detailsSubmitted);
  const stripeReady = Boolean(stripe.connectedAccountId && stripe.chargesEnabled && stripe.payoutsEnabled);
  const stripeReviewPending = Boolean(stripe.connectedAccountId && stripeDetailsSubmitted && !stripeReady);
  const paymentEnvironment = paymentSettings?.environment || "not_configured";
  const setupNeedsAttention = paymentSettings?.configured === false ||
    paymentSettings?.publishableKeyConfigured === false ||
    !stripe.connectedAccountId ||
    !stripeDetailsSubmitted ||
    !stripe.payoutsEnabled;
  const attentionCount = pendingReceipts.length + attentionEvents.length + issues.length + (setupNeedsAttention ? 1 : 0);
  const rangeReceipts = useMemo(
    () => receipts.filter((receipt) => isWithinDateRange(recordDate(receipt), dateRange)),
    [dateRange, receipts]
  );
  const rangeSummary = useMemo(
    () => summarizePaidReceipts(rangeReceipts, () => true),
    [rangeReceipts]
  );
  const rangeLineTotals = useMemo(() => businessLineTotals(rangeReceipts), [rangeReceipts]);
  const rangeRevenueLines = rangeLineTotals.filter((line) => line.count > 0 || line.amountCents > 0);
  const revenuePlotSeries = useMemo(() => buildRevenuePlotData(rangeReceipts), [rangeReceipts]);
  const rangeLabel = formatDateRangeLabel(dateRange);
  const historyDateRows = useMemo(
    () => moneyRows.filter((row) => isWithinDateRange(row.date, historyDateRange)),
    [historyDateRange, moneyRows]
  );
  const paymentHistoryRows = useMemo(
    () => (moneyHistoryLookup.trim() ? searchMoneyRows(historyDateRows, moneyHistoryLookup) : historyDateRows),
    [historyDateRows, moneyHistoryLookup]
  );
  const paymentHistoryTotal = paymentHistoryRows.reduce((total, row) => total + Number(row.amountCents || 0), 0);
  const periodCloseReceipts = useMemo(
    () => historyDateRows
      .filter((row) => row.source === "Receipt")
      .map((row) => row.raw || {})
      .filter(isPeriodCloseReceipt),
    [historyDateRows]
  );
  const periodCloseLedgerNetCents = useMemo(
    () => periodCloseReceipts.reduce((total, receipt) => total + receiptLedgerNetCents(receipt), 0),
    [periodCloseReceipts]
  );
  const historyRangeLabel = formatDateRangeLabel(historyDateRange);
  const stripeCanOpenEmbedded = Boolean(
    stripe.connectedAccountId &&
    paymentSettings?.configured !== false &&
    paymentSettings?.publishableKeyConfigured !== false
  );
  const primaryStripeLabel = startingStripeConnect
    ? "Opening..."
    : stripe.connectedAccountId
      ? stripeDetailsSubmitted ? "Open Stripe account" : "Finish Stripe setup"
      : "Set up Stripe";
  const connectComponentOptions = useMemo(() => {
    const operationalTabs = [
      { key: "payments", label: "Transactions" },
      { key: "balances", label: "Balance" },
      { key: "payouts", label: "Payouts" },
      { key: "account-management", label: "Account details" }
    ];

    if (!stripeDetailsSubmitted) {
      return [
        { key: "account-onboarding", label: "Setup" },
        ...operationalTabs
      ];
    }

    return operationalTabs;
  }, [stripeDetailsSubmitted]);
  const paymentSections = useMemo(() => [
    { id: "overview", label: "Overview", meta: stripeReady ? "Ready" : "Action" },
    { id: "money", label: "Payment history", meta: formatMoney(paidTotal) },
    { id: "stripe", label: "Stripe account", meta: stripeReady ? "Ready" : stripeReviewPending ? "In review" : "Requirements" }
  ], [paidTotal, stripeReady, stripeReviewPending]);
  const launchReadinessItems = useMemo(() => [
    {
      key: "account",
      label: "Stripe account connected",
      detail: stripe.connectedAccountId ? "Range payments have a Stripe account." : "Connect Stripe before taking online payments.",
      status: stripe.connectedAccountId ? "done" : "action"
    },
    {
      key: "details",
      label: "Business details submitted",
      detail: stripeDetailsSubmitted ? "Stripe has the required business profile." : "Finish the business profile in Stripe.",
      status: stripeDetailsSubmitted ? "done" : stripe.connectedAccountId ? "action" : "waiting"
    },
    {
      key: "charges",
      label: "Checkout can charge cards",
      detail: stripe.chargesEnabled ? "Member checkout can collect card payments." : "Stripe has not enabled card charges yet.",
      status: stripe.chargesEnabled ? "done" : stripeDetailsSubmitted ? "waiting" : "action"
    },
    {
      key: "payouts",
      label: "Payouts are enabled",
      detail: stripe.payoutsEnabled ? "Collected funds can be paid out." : "Stripe has not enabled payouts yet.",
      status: stripe.payoutsEnabled ? "done" : stripeDetailsSubmitted ? "waiting" : "action"
    },
    {
      key: "mode",
      label: "Live mode selected",
      detail: paymentEnvironment === "live" ? "This workspace is using live Stripe credentials." : "Current payments are suitable for testing and demos only.",
      status: paymentEnvironment === "live" ? "done" : "action"
    }
  ], [paymentEnvironment, stripe.chargesEnabled, stripe.connectedAccountId, stripe.payoutsEnabled, stripeDetailsSubmitted]);

  useEffect(() => {
    connectClientSecretRef.current = accountSession?.clientSecret || "";
  }, [accountSession?.clientSecret]);

  const fetchConnectClientSecret = useCallback(async () => {
    if (connectClientSecretRef.current) {
      return connectClientSecretRef.current;
    }

    if (typeof onFetchStripeConnectClientSecret !== "function") {
      throw new Error("Stripe did not return an embedded workspace session.");
    }

    const nextClientSecret = await onFetchStripeConnectClientSecret();
    connectClientSecretRef.current = nextClientSecret || "";
    return nextClientSecret;
  }, [onFetchStripeConnectClientSecret]);

  function switchPaymentSection(sectionId) {
    const normalized = normalizePaymentSection(sectionId);
    setActivePaymentSection(normalized);
    if (typeof window !== "undefined") {
      const nextHash = `#payments-${normalized}`;
      if (window.location.hash !== nextHash) {
        window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${nextHash}`);
      }
    }
  }

  async function handleOpenStripeWorkspace() {
    await onStartStripeConnect?.();
    switchPaymentSection("stripe");
  }

  function handleDatePresetChange(value) {
    setDatePreset(value);
    setDateRange(getPresetDateRange(value, receipts));
  }

  function handleDateRangeChange(field, value) {
    setDatePreset("custom");
    setDateRange((current) => ({
      ...current,
      [field]: value
    }));
  }

  function handleHistoryPresetChange(value) {
    setHistoryDatePreset(value);
    setHistoryDateRange(getPresetDateRange(value, receipts));
  }

  function handleHistoryDateRangeChange(field, value) {
    setHistoryDatePreset("custom");
    setHistoryDateRange((current) => ({
      ...current,
      [field]: value
    }));
  }

  function openRefundEditor(row) {
    const remainingCents = refundableAmountCents(row.raw || {});
    setRefundError("");
    setRefundEditorKey(row.key);
    setRefundDraft({
      amount: (remainingCents / 100).toFixed(2),
      reason: "requested_by_customer",
      note: ""
    });
  }

  function closeRefundEditor() {
    setRefundEditorKey("");
    setRefundError("");
    setRefundDraft({
      amount: "",
      reason: "requested_by_customer",
      note: ""
    });
  }

  function openReconciliationEditor(row) {
    const review = receiptReconciliationReview(row.raw || {});
    setReconciliationEditorKey(row.key);
    setReconciliationDraft({
      status: review.status || "reviewed",
      note: review.note || ""
    });
    setReconciliationError("");
  }

  function closeReconciliationEditor() {
    setReconciliationEditorKey("");
    setReconciliationDraft({
      status: "reviewed",
      note: ""
    });
    setReconciliationError("");
  }

  async function submitRefund(event, row) {
    event.preventDefault();
    const amountCents = Math.round(Number.parseFloat(refundDraft.amount || "0") * 100);
    if (!Number.isFinite(amountCents) || amountCents <= 0) {
      setRefundError("Enter a refund amount greater than zero.");
      return;
    }
    if (amountCents > refundableAmountCents(row.raw || {})) {
      setRefundError("Refund amount cannot exceed the remaining payment amount.");
      return;
    }
    setRefundError("");
    try {
      await onRefundReceipt?.(row.raw, {
        amountCents,
        reason: refundDraft.reason,
        note: refundDraft.note
      });
      closeRefundEditor();
    } catch (err) {
      setRefundError(err.message || "RangeGrid could not create this refund.");
    }
  }

  async function submitReconciliationReview(event, row) {
    event.preventDefault();
    setReconciliationError("");
    try {
      await onUpdateReceiptReconciliation?.(row.raw, reconciliationDraft);
      closeReconciliationEditor();
    } catch (err) {
      setReconciliationError(err.message || "RangeGrid could not update this review.");
    }
  }

  async function syncStripeLedger() {
    setReconciliationSyncError("");
    setReconciliationSyncResult(null);
    try {
      const result = await onSyncReconciliation?.(historyDateRange);
      setReconciliationSyncResult(result?.summary || { checked: 0, updated: 0, failed: 0 });
    } catch (err) {
      setReconciliationSyncError(err.message || "RangeGrid could not sync Stripe ledger data.");
    }
  }

  function openPeriodCloseForm() {
    setPeriodCloseOpen(true);
    setPeriodCloseError("");
    setPeriodCloseResult(null);
    setPeriodCloseDraft({
      depositAmount: (periodCloseLedgerNetCents / 100).toFixed(2),
      depositDate: new Date().toISOString().slice(0, 10),
      depositReference: ""
    });
  }

  function closePeriodCloseForm() {
    setPeriodCloseOpen(false);
    setPeriodCloseError("");
  }

  async function submitPeriodClose(event) {
    event.preventDefault();
    const depositAmountCents = Math.round(Number.parseFloat(periodCloseDraft.depositAmount || "0") * 100);
    if (!Number.isFinite(depositAmountCents) || depositAmountCents < 0) {
      setPeriodCloseError("Enter the deposit amount in dollars.");
      return;
    }
    if (!periodCloseDraft.depositDate) {
      setPeriodCloseError("Choose the bank deposit date.");
      return;
    }
    setPeriodCloseError("");
    try {
      const result = await onCloseReconciliation?.({
        startDate: historyDateRange.start,
        endDate: historyDateRange.end,
        depositAmountCents,
        depositDate: periodCloseDraft.depositDate,
        depositReference: periodCloseDraft.depositReference
      });
      setPeriodCloseResult(result?.close || null);
      setPeriodCloseOpen(false);
    } catch (err) {
      setPeriodCloseError(err.message || "RangeGrid could not close this period.");
    }
  }

  useEffect(() => {
    function syncSectionFromHash() {
      const hash = window.location.hash.replace("#payments-", "");
      if (hash) {
        const normalized = normalizePaymentSection(hash);
        setActivePaymentSection(normalized);
        if (normalized !== hash) {
          window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#payments-${normalized}`);
        }
      }
    }

    syncSectionFromHash();
    window.addEventListener("hashchange", syncSectionFromHash);
    return () => {
      window.removeEventListener("hashchange", syncSectionFromHash);
    };
  }, []);

  useEffect(() => {
    if (activePaymentSection !== "stripe") {
      setAutoStripeSessionRequested(false);
    }
  }, [activePaymentSection]);

  useEffect(() => {
    if (
      activePaymentSection !== "stripe" ||
      connectReady ||
      !stripeCanOpenEmbedded ||
      stripeSettingsLoading ||
      startingStripeConnect ||
      autoStripeSessionRequested ||
      typeof onStartStripeConnect !== "function"
    ) {
      return;
    }

    let cancelled = false;
    setAutoStripeSessionRequested(true);
    Promise.resolve(onStartStripeConnect()).catch(() => {
      if (!cancelled) {
        setAutoStripeSessionRequested(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    activePaymentSection,
    autoStripeSessionRequested,
    connectReady,
    onStartStripeConnect,
    startingStripeConnect,
    stripeCanOpenEmbedded,
    stripeSettingsLoading
  ]);

  useEffect(() => {
    if (connectReady && !stripeDetailsSubmitted) {
      setConnectComponent("account-onboarding");
      return;
    }

    if (connectReady && stripeDetailsSubmitted && connectComponent === "account-onboarding") {
      setConnectComponent(stripeReady ? "balances" : "account-management");
    }
  }, [connectComponent, connectReady, stripeDetailsSubmitted, stripeReady]);

  useEffect(() => {
    if (!connectReady || connectLoadError || !connectLoading) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setConnectLoading(false);
    }, 3500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [connectComponent, connectLoadError, connectLoading, connectReady]);

  useEffect(() => {
    if (!connectReady) {
      setConnectInstance(null);
      setConnectLoading(false);
      return undefined;
    }

    let cancelled = false;
    setConnectLoadError("");
    setConnectLoading(true);
    try {
      const instance = loadConnectAndInitialize({
        publishableKey: accountSession.publishableKey,
        fetchClientSecret: async () => {
          try {
            return await fetchConnectClientSecret();
          } catch (err) {
            const message = err.message || "Stripe account tools could not refresh the secure session.";
            if (!cancelled) {
              setConnectLoadError(message);
              setConnectLoading(false);
            }
            throw err;
          }
        },
        appearance: {
          overlays: "dialog",
          variables: {
            colorPrimary: "#2563eb",
            borderRadius: "8px"
          }
        }
      });
      if (!cancelled) {
        setConnectInstance(instance);
      }
    } catch (err) {
      if (!cancelled) {
        setConnectLoadError(err.message || "Stripe account tools could not load.");
        setConnectLoading(false);
      }
    }

    return () => {
      cancelled = true;
    };
  }, [accountSession?.clientSecret, accountSession?.publishableKey, connectReady, fetchConnectClientSecret]);

  const connectCollectionOptions = useMemo(() => ({
    fields: "eventually_due",
    futureRequirements: "include"
  }), []);

  const stripeComponentProps = useMemo(() => ({
    onLoaderStart: () => {
      setConnectLoading(true);
    },
    onLoadError: ({ error }) => {
      setConnectLoading(false);
      setConnectLoadError(error?.message || "Stripe account tools could not load.");
    }
  }), []);

  const renderStripeWorkspaceComponent = () => {
    switch (connectComponent) {
      case "payments":
        return <ConnectPayments {...stripeComponentProps} />;
      case "balances":
        return <ConnectBalances {...stripeComponentProps} />;
      case "payouts":
        return <ConnectPayouts {...stripeComponentProps} />;
      case "account-management":
        return <ConnectAccountManagement {...stripeComponentProps} collectionOptions={connectCollectionOptions} />;
      case "account-onboarding":
      default:
        return (
          <ConnectAccountOnboarding
            {...stripeComponentProps}
            collectionOptions={connectCollectionOptions}
            onExit={onStripeConnectExit}
            onStepChange={() => {
              setConnectLoading(false);
            }}
          />
        );
    }
  };

  if (!selectedRange) {
    return (
      <section className="panel page-panel">
        <div className="empty-state">Select a range before opening payments.</div>
      </section>
    );
  }

  return (
    <div className="settings-stack payments-workspace">
      <nav className="console-section-nav page-section-nav" aria-label="Payment sections">
        {paymentSections.map((section) => (
          <button
            type="button"
            className={activePaymentSection === section.id ? "is-active" : ""}
            key={section.id}
            onClick={() => switchPaymentSection(section.id)}
          >
            <span>{section.label}</span>
            <small>{section.meta}</small>
          </button>
        ))}
      </nav>

      {activePaymentSection === "overview" && (
      <section className="panel page-panel payments-command-center">
        <div className="widget-title-row">
          <div className="panel-header">
            <h2>Payments at a glance</h2>
            <p>{selectedRange.name} payment activity and online payment health. Detailed lookup lives in Payment history.</p>
          </div>
          <div className={`payments-health-badge ${stripeReady ? "is-ready" : "needs-action"}`}>
            <span>{stripeReady ? "Checkout" : "Needs action"}</span>
            <strong>{stripeReady ? "Ready" : attentionCount}</strong>
          </div>
        </div>

        <RevenuePlot
          datePreset={datePreset}
          dateRange={dateRange}
          onDatePresetChange={handleDatePresetChange}
          onDateRangeChange={handleDateRangeChange}
          rangeLabel={rangeLabel}
          revenueLines={rangeRevenueLines}
          series={revenuePlotSeries}
          summary={rangeSummary}
        />

        <PaymentLaunchChecklist
          environment={paymentEnvironment}
          items={launchReadinessItems}
        />

        {issues.length > 0 && (
          <div className="payments-processing-issues" aria-label="Payment processing issues">
            <div className="payments-processing-issues-header">
              <div>
                <h3>Payment processing needs review</h3>
                <p>Stripe sent updates that RangeGrid could not finish processing.</p>
              </div>
              <span>{issues.length}</span>
            </div>
            <div className="payments-processing-issues-list">
              {issues.map((issue) => (
                <div className="payments-processing-issue" key={issue.issueId}>
                  <div>
                    <strong>{issue.title || "Payment processing needs review"}</strong>
                    <small>{issue.eventType || "Payment update"} / {formatDateTime(issue.lastUpdatedAt || issue.firstSeenAt)}</small>
                  </div>
                  <p>{issue.message || "RangeGrid could not finish processing this payment update."}</p>
                  <span>{issue.action || "Check payment history and Stripe activity."}</span>
                </div>
              ))}
            </div>
          </div>
        )}

      </section>
      )}

      {activePaymentSection === "money" && (
      <section className="panel page-panel payments-history-panel">
        <div className="panel-header">
          <h2>Payment history</h2>
          <p>Review collected payments, open payment records, and receipt downloads in one place.</p>
        </div>
        <div className="payments-history-toolbar">
          <div className="payments-history-primary-row">
            <label className="payments-support-search">
              <span>Find a payment</span>
              <input
                type="search"
                value={moneyHistoryLookup}
                onChange={(event) => setMoneyHistoryLookup(event.target.value)}
                placeholder="Customer, item, status"
              />
            </label>
            <button
              type="button"
              className="secondary-button"
              disabled={exportingReceipts || receipts.length === 0}
              onClick={() => onExportReceipts?.(historyDateRange)}
            >
              {exportingReceipts ? "Exporting..." : "Export payment records"}
            </button>
            <button
              type="button"
              className="secondary-button"
              disabled={exportingReconciliation || receipts.length === 0}
              onClick={() => onExportReconciliation?.(historyDateRange)}
            >
              {exportingReconciliation ? "Exporting..." : "Export reconciliation file"}
            </button>
            <button
              type="button"
              className="secondary-button"
              disabled={syncingReconciliation || receipts.length === 0}
              onClick={syncStripeLedger}
            >
              {syncingReconciliation ? "Syncing..." : "Sync Stripe ledger"}
            </button>
            <button
              type="button"
              className="secondary-button"
              disabled={periodCloseReceipts.length === 0}
              onClick={openPeriodCloseForm}
            >
              Close period
            </button>
          </div>

          {(reconciliationSyncResult || reconciliationSyncError) && (
            <div className={`payments-sync-note ${reconciliationSyncError ? "is-error" : ""}`}>
              {reconciliationSyncError || `${reconciliationSyncResult.updated || 0} receipts updated from Stripe. ${reconciliationSyncResult.failed || 0} need follow-up.`}
            </div>
          )}

          {periodCloseResult && (
            <div className={`payments-sync-note ${periodCloseResult.status === "matched" ? "" : "is-error"}`}>
              {periodCloseResult.status === "matched"
                ? `Period closed. Bank deposit matches ${formatMoney(periodCloseResult.ledgerNetCents)}.`
                : `Period needs follow-up. Difference: ${formatMoney(periodCloseResult.varianceCents)}.`}
            </div>
          )}

          {periodCloseOpen && (
            <form className="payments-period-close-form" onSubmit={submitPeriodClose}>
              <div>
                <span>Period</span>
                <strong>{historyRangeLabel}</strong>
                <small>{periodCloseReceipts.length} receipts · ledger net {formatMoney(periodCloseLedgerNetCents)}</small>
              </div>
              <label>
                <span>Bank deposit</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={periodCloseDraft.depositAmount}
                  onChange={(event) => setPeriodCloseDraft((current) => ({ ...current, depositAmount: event.target.value }))}
                />
              </label>
              <label>
                <span>Deposit date</span>
                <input
                  type="date"
                  value={periodCloseDraft.depositDate}
                  onChange={(event) => setPeriodCloseDraft((current) => ({ ...current, depositDate: event.target.value }))}
                />
              </label>
              <label>
                <span>Reference</span>
                <input
                  value={periodCloseDraft.depositReference}
                  onChange={(event) => setPeriodCloseDraft((current) => ({ ...current, depositReference: event.target.value }))}
                  placeholder="Optional bank memo"
                />
              </label>
              <div className="payment-refund-actions">
                <button type="button" className="secondary-button" onClick={closePeriodCloseForm} disabled={closingReconciliation}>
                  Cancel
                </button>
                <button type="submit" disabled={closingReconciliation}>
                  {closingReconciliation ? "Closing..." : "Save close"}
                </button>
              </div>
              {periodCloseError && <div className="payment-refund-error">{periodCloseError}</div>}
            </form>
          )}

          <div className="payments-history-filter-row">
            <label className="payments-date-field">
              <span>Quick range</span>
              <select value={historyDatePreset} onChange={(event) => handleHistoryPresetChange(event.target.value)}>
                {historyDatePreset === "custom" && <option value="custom">Custom dates</option>}
                {PAYMENT_DATE_PRESETS.map((preset) => (
                  <option key={preset.key} value={preset.key}>{preset.label}</option>
                ))}
              </select>
            </label>

            <label className="payments-date-field">
              <span>Start date</span>
              <input
                type="date"
                value={historyDateRange.start}
                onChange={(event) => handleHistoryDateRangeChange("start", event.target.value)}
              />
            </label>

            <label className="payments-date-field">
              <span>End date</span>
              <input
                type="date"
                value={historyDateRange.end}
                onChange={(event) => handleHistoryDateRangeChange("end", event.target.value)}
              />
            </label>

            <div className="payments-history-summary">
              <strong>{paymentHistoryRows.length}</strong>
              <span>{moneyHistoryLookup.trim() ? "matching records" : "records shown"}</span>
              <small>{formatMoney(paymentHistoryTotal)} in {historyRangeLabel}</small>
            </div>
          </div>

          {periodCloses.length > 0 && (
            <div className="payments-period-close-history">
              <div className="payments-period-close-history-header">
                <div>
                  <strong>Closed periods</strong>
                  <span>Recent bank deposit matches for this workspace.</span>
                </div>
                <button
                  type="button"
                  className="secondary-button compact-action-button"
                  disabled={exportingReconciliation}
                  onClick={() => onExportPeriodCloses?.()}
                >
                  {exportingReconciliation ? "Exporting..." : "Export closes"}
                </button>
              </div>
              <div className="payments-period-close-list">
                {periodCloses.slice(0, 4).map((close) => (
                  <div className="payments-period-close-row" key={close.closeId || `${close.startDate}-${close.endDate}`}>
                    <div>
                      <strong>{close.startDate} to {close.endDate}</strong>
                      <small>{close.receiptCount || 0} receipts · closed {formatDateTime(close.closedAt)}</small>
                    </div>
                    <span>{close.status === "matched" ? "Matched" : "Needs follow-up"}</span>
                    <div>
                      <strong>{formatMoney(close.depositAmountCents || 0)}</strong>
                      <small>deposit</small>
                    </div>
                    <div>
                      <strong>{formatMoney(close.varianceCents || 0)}</strong>
                      <small>difference</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {moneyRows.length === 0 && !loading ? (
          <PaymentEmptyState
            title="No payment records yet"
            detail="Paid memberships, reservations, classes, and donations will appear here after checkout confirmation."
          />
        ) : paymentHistoryRows.length === 0 ? (
          <PaymentEmptyState
            title="No matching payment records"
            detail="Adjust the search or date range to widen the results."
          />
        ) : (
          <div className="table-like compact-table payment-history-table" role="table" aria-label="Payment history">
            <div className="table-row table-head" role="row">
              <span>Payment</span>
              <span>Customer</span>
              <span>Status</span>
              <span>Collected</span>
              <span>Actions</span>
            </div>
            {paymentHistoryRows.map((row) => {
              const refundedCents = refundedAmountCents(row.raw || {});
              const canRefund = canRefundMoneyRow(row);
              const refunding = refundingReceiptId && refundingReceiptId === row.raw?.receiptId;
              const review = receiptReconciliationReview(row.raw || {});
              const reconciling = reconcilingReceiptId && reconcilingReceiptId === row.raw?.receiptId;
              return (
                <div className="payment-history-row-wrap" key={row.key}>
                  <div className="table-row" role="row">
                    <span>
                      <strong>{row.title}</strong>
                      <small>{ownerLineLabel(row.line)}</small>
                    </span>
                    <span>{row.customer}</span>
                    <span><PaymentStatusChip status={row.status} /></span>
                    <span>
                      <strong>{formatMoney(row.amountCents, row.currency)}</strong>
                      <small>{formatDateTime(row.date)}</small>
                    </span>
                    <span className="payment-history-receipt-cell">
                      {row.source === "Receipt" ? (
                        <>
                          <button type="button" className="secondary-button compact-action-button" onClick={() => onDownloadReceipt(row.raw)}>
                            Download
                          </button>
                          <button
                            type="button"
                            className="secondary-button compact-action-button"
                            onClick={() => openReconciliationEditor(row)}
                            disabled={Boolean(reconcilingReceiptId)}
                          >
                            {reconciling ? "Saving..." : reconciliationReviewLabel(review.status)}
                          </button>
                          {canRefund ? (
                            <button
                              type="button"
                              className="secondary-button compact-action-button"
                              onClick={() => openRefundEditor(row)}
                              disabled={Boolean(refundingReceiptId)}
                            >
                              {refunding ? "Refunding..." : "Refund"}
                            </button>
                          ) : refundedCents > 0 ? (
                            <small>{formatMoney(refundedCents, row.currency)} refunded</small>
                          ) : null}
                        </>
                      ) : (
                        <small>System record</small>
                      )}
                    </span>
                  </div>
                  {refundEditorKey === row.key && (
                    <form className="payment-refund-editor" onSubmit={(event) => submitRefund(event, row)}>
                      <label>
                        <span>Refund amount</span>
                        <input
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={refundDraft.amount}
                          onChange={(event) => setRefundDraft((current) => ({ ...current, amount: event.target.value }))}
                        />
                      </label>
                      <label>
                        <span>Reason</span>
                        <select
                          value={refundDraft.reason}
                          onChange={(event) => setRefundDraft((current) => ({ ...current, reason: event.target.value }))}
                        >
                          <option value="requested_by_customer">Customer request</option>
                          <option value="duplicate">Duplicate payment</option>
                          <option value="fraudulent">Fraudulent payment</option>
                        </select>
                      </label>
                      <label className="payment-refund-note">
                        <span>Owner note</span>
                        <input
                          value={refundDraft.note}
                          onChange={(event) => setRefundDraft((current) => ({ ...current, note: event.target.value }))}
                          placeholder="Optional internal note"
                        />
                      </label>
                      <div className="payment-refund-actions">
                        <button type="button" className="secondary-button" onClick={closeRefundEditor} disabled={Boolean(refundingReceiptId)}>
                          Cancel
                        </button>
                        <button type="submit" disabled={Boolean(refundingReceiptId)}>
                          {refunding ? "Creating refund..." : "Create refund"}
                        </button>
                      </div>
                      {refundError && <div className="payment-refund-error">{refundError}</div>}
                    </form>
                  )}
                  {reconciliationEditorKey === row.key && (
                    <form className="payment-refund-editor" onSubmit={(event) => submitReconciliationReview(event, row)}>
                      <label>
                        <span>Review status</span>
                        <select
                          value={reconciliationDraft.status}
                          onChange={(event) => setReconciliationDraft((current) => ({ ...current, status: event.target.value }))}
                        >
                          <option value="reviewed">Reviewed</option>
                          <option value="needs_follow_up">Needs follow-up</option>
                          <option value="open">Open review</option>
                        </select>
                      </label>
                      <label className="payment-refund-note">
                        <span>Owner note</span>
                        <input
                          value={reconciliationDraft.note}
                          onChange={(event) => setReconciliationDraft((current) => ({ ...current, note: event.target.value }))}
                          placeholder="Optional internal note"
                        />
                      </label>
                      <div className="payment-refund-actions">
                        <button type="button" className="secondary-button" onClick={closeReconciliationEditor} disabled={Boolean(reconcilingReceiptId)}>
                          Cancel
                        </button>
                        <button type="submit" disabled={Boolean(reconcilingReceiptId)}>
                          {reconciling ? "Saving..." : "Save review"}
                        </button>
                      </div>
                      {reconciliationError && <div className="payment-refund-error">{reconciliationError}</div>}
                    </form>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
      )}

      {activePaymentSection === "stripe" && (
      <section className="panel page-panel payments-stripe-panel">
        <div className="panel-header">
          <h2>Stripe account</h2>
          <p>Manage account requirements, balances, payouts, and processor-side payment details.</p>
        </div>
        <PaymentLaunchChecklist
          environment={paymentEnvironment}
          items={launchReadinessItems}
        />
        {stripeCanOpenEmbedded && !connectReady ? (
          <div className="stripe-loading-state stripe-auto-loading">
            {startingStripeConnect || autoStripeSessionRequested ? "Loading Stripe account tools..." : "Preparing Stripe account tools..."}
          </div>
        ) : !connectReady ? (
          <div className="stripe-setup-card">
            <div>
              <span>Stripe Connect</span>
              <strong>
                {stripeSettingsLoading
                  ? "Loading Stripe account"
                  : stripe.connectedAccountId ? "Open Stripe account tools" : "Set up Stripe account"}
              </strong>
              <p>{stripeSettingsLoading ? "Checking current Stripe status." : "Open a secure embedded Stripe workspace for this range."}</p>
            </div>
            <div className="payment-action-row">
              <button
                type="button"
                disabled={stripeSettingsLoading || startingStripeConnect || paymentSettings?.configured === false}
                onClick={handleOpenStripeWorkspace}
              >
                {primaryStripeLabel}
              </button>
              {paymentSettings?.configured === false && (
                <span>Online payment setup is not ready for this workspace yet.</span>
              )}
              {paymentSettings?.publishableKeyConfigured === false && (
                <span>Stripe account tools are not available until payment setup is finished.</span>
              )}
            </div>
          </div>
        ) : (
          <>
            {stripeReviewPending && (
              <div className="notice stripe-review-notice">
                <div>
                  <strong>Stripe is reviewing the submitted account information.</strong>
                  <span>Checkout stays paused until Stripe finishes review. Refresh status after a few minutes if this page has been open.</span>
                </div>
                {typeof onRefreshStripeConnect === "function" && (
                  <button
                    type="button"
                    className="secondary-button"
                    disabled={refreshingStripeConnect}
                    onClick={onRefreshStripeConnect}
                  >
                    {refreshingStripeConnect ? "Refreshing..." : "Refresh status"}
                  </button>
                )}
              </div>
            )}
            <div className="segmented-control payment-dashboard-tabs">
              {connectComponentOptions.map((item) => (
                <button
                  type="button"
                  key={item.key}
                  className={connectComponent === item.key ? "active" : ""}
                  onClick={() => setConnectComponent(item.key)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            {connectLoadError ? (
              <div className="notice warning-notice">{connectLoadError}</div>
            ) : (
              <div className="stripe-workspace-shell">
                {connectLoading && !connectInstance && (
                  <div className="stripe-loading-state stripe-loading-card" aria-live="polite">
                    <span />
                    <span />
                    <span />
                  </div>
                )}
                {connectInstance && (
                  <ConnectComponentsProvider connectInstance={connectInstance}>
                    <div className="stripe-embedded-component">
                      <ConnectNotificationBanner
                        {...stripeComponentProps}
                        collectionOptions={connectCollectionOptions}
                      />
                      {renderStripeWorkspaceComponent()}
                    </div>
                  </ConnectComponentsProvider>
                )}
              </div>
            )}
          </>
        )}
      </section>
      )}
    </div>
  );
}
